/**
 * usePatientDetail.ts
 *
 * Patient sidebar data hooks. Each concern (core, tags, primary note,
 * memberships, appointments) is its own React Query hook so the right-rail
 * sections paint independently as their fetch resolves, can be invalidated
 * granularly, and are auto-cancelled when the user clicks a different patient.
 *
 * `usePatientDetail` is kept as a backward-compatible aggregator that returns
 * the merged `PatientDetail` shape used by existing consumers.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '@/services/apiClient';
import {
  EMPTY_PATIENT_DETAIL,
  normalizePatientAppointments,
  normalizePatientMemberships,
  normalizePatientTags,
  type PatientDetail,
  type PatientSidebarAppointment,
  type PatientSidebarMembership,
  type PatientSidebarTag,
} from '@/lib/patientDetail';
import { getPatientSidebarStats } from '@/services/patients.service';
import { useMembershipTemplates } from './useMembershipTemplates';

// ─── Cache configuration ─────────────────────────────────────────────────────

const PATIENT_SIDEBAR_STALE_TIME = 60_000;

export const patientQueryKeys = {
  all: ['patient'] as const,
  core: (id: string) => ['patient', id, 'core'] as const,
  tags: (id: string) => ['patient', id, 'tags'] as const,
  note: (id: string) => ['patient', id, 'note'] as const,
  memberships: (id: string) => ['patient', id, 'memberships'] as const,
  appointments: (id: string) => ['patient', id, 'appointments'] as const,
};

// ─── Individual hooks ────────────────────────────────────────────────────────

export function usePatientCore(patientId: string | null) {
  return useQuery({
    queryKey: patientQueryKeys.core(patientId ?? ''),
    queryFn: async () => {
      const r = await apiClient.get(`/patients/${patientId}`);
      return (r.data?.data as Record<string, unknown> | undefined) ?? null;
    },
    enabled: !!patientId,
    staleTime: PATIENT_SIDEBAR_STALE_TIME,
  });
}

export function usePatientTags(patientId: string | null) {
  return useQuery({
    queryKey: patientQueryKeys.tags(patientId ?? ''),
    queryFn: async () => {
      const r = await apiClient.get(`/patients/${patientId}/tags`);
      return normalizePatientTags(r.data);
    },
    enabled: !!patientId,
    staleTime: PATIENT_SIDEBAR_STALE_TIME,
  });
}

export function usePatientPrimaryNote(patientId: string | null) {
  return useQuery({
    queryKey: patientQueryKeys.note(patientId ?? ''),
    queryFn: async () => {
      const r = await apiClient.get(`/patients/${patientId}/primarynote`);
      const data = r.data?.data as { note?: string } | null | undefined;
      return data?.note ?? '';
    },
    enabled: !!patientId,
    staleTime: PATIENT_SIDEBAR_STALE_TIME,
  });
}

export function usePatientMemberships(patientId: string | null): {
  data: PatientSidebarMembership[];
  isLoading: boolean;
  isError: boolean;
} {
  const templates = useMembershipTemplates();
  const query = useQuery({
    queryKey: patientQueryKeys.memberships(patientId ?? ''),
    queryFn: async () => {
      const r = await apiClient.get(`/memberships/patients/${patientId}`);
      return r.data;
    },
    enabled: !!patientId,
    staleTime: PATIENT_SIDEBAR_STALE_TIME,
  });

  const data = useMemo(
    () => normalizePatientMemberships(query.data, templates.data),
    [query.data, templates.data],
  );

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function usePatientAppointments(patientId: string | null) {
  return useQuery({
    queryKey: patientQueryKeys.appointments(patientId ?? ''),
    queryFn: async () => {
      const r = await apiClient.get(`/patients/${patientId}/appointments`);
      return normalizePatientAppointments(r.data);
    },
    enabled: !!patientId,
    staleTime: PATIENT_SIDEBAR_STALE_TIME,
  });
}

/**
 * Invalidates every cached view of a patient's appointments.
 *
 * The same appointments are read through two unrelated query keys: the sidebar
 * cards (Upcoming Appointments / Recent Visits) use
 * `patientQueryKeys.appointments`, while the Appointments tab on
 * PatientDetailPage uses `['patient-appointments', id]`. Saving from a modal
 * only ever refreshed one of them, so the other kept showing stale cards until
 * a full page reload. Every appointment save/delete should call this so the two
 * views can't disagree.
 */
export function invalidatePatientAppointments(
  queryClient: QueryClient,
  patientId: number | string,
): void {
  const idStr = String(patientId);
  const idNum = Number(patientId);

  // refetchType 'all' so a section that is currently scrolled out of view (its
  // query is disabled until `useInViewport` fires) still refetches rather than
  // sitting on the pre-save rows when it comes back.
  const opts = { refetchType: 'all' } as const;

  void queryClient.invalidateQueries({ queryKey: patientQueryKeys.appointments(idStr), ...opts });
  void queryClient.invalidateQueries({ queryKey: ['patient-appointments', idNum], ...opts });
  void queryClient.invalidateQueries({ queryKey: ['patient-appointments-products', idNum], ...opts });
  // Sidebar stat tiles count appointments too, so they drift the same way.
  void queryClient.invalidateQueries({ queryKey: ['patient-sidebar-stats', idNum], ...opts });
}

// ─── Hover prefetch ──────────────────────────────────────────────────────────

/**
 * Warms the React Query cache with the slowest endpoints used by the patient
 * sidebar. Intended to be called from `onMouseEnter` on patient rows so the
 * AI summary in particular has a head start before the user clicks.
 */
export function prefetchPatientSidebar(
  queryClient: QueryClient,
  patientId: number,
): void {
  if (!patientId || patientId <= 0) return;

  const idStr = String(patientId);

  void queryClient.prefetchQuery({
    queryKey: patientQueryKeys.core(idStr),
    queryFn: async () => {
      const r = await apiClient.get(`/patients/${idStr}`);
      return (r.data?.data as Record<string, unknown> | undefined) ?? null;
    },
    staleTime: PATIENT_SIDEBAR_STALE_TIME,
  });

  void queryClient.prefetchQuery({
    queryKey: ['patient-sidebar-stats', patientId],
    queryFn: () => getPatientSidebarStats(patientId),
    staleTime: PATIENT_SIDEBAR_STALE_TIME,
  });

  // The AI patient context (['ai-context','patient',id]) is deliberately NOT
  // prefetched here. That endpoint generates its summary with an LLM call, so
  // warming it on hover billed one call per row the mouse passed over. It now
  // loads only when the user asks the assistant for something.
}

// ─── Backward-compatible aggregator ──────────────────────────────────────────

/**
 * Returns the above-the-fold patient detail data (core, tags, note).
 *
 * The bottom-of-sidebar fields on `PatientDetail` (`memberships`,
 * `upcomingAppointments`, `recentAppointments`) are intentionally returned as
 * empty arrays here — those sections fetch their own data lazily on viewport
 * intersection inside `DetailProfile`, so we avoid eagerly issuing requests
 * the user may never scroll to.
 */
export function usePatientDetail(patientId: string | null): {
  detail: PatientDetail;
  loading: boolean;
  /**
   * The core patient fetch failed and there is no patient to show (a 404 for a
   * patient outside the selected practice, a network error). Lets a section
   * that waits on demographics stop waiting (SCRUM-2006).
   */
  failed: boolean;
} {
  const core = usePatientCore(patientId);
  const tags = usePatientTags(patientId);
  const note = usePatientPrimaryNote(patientId);

  const detail = useMemo<PatientDetail>(() => {
    const tagsValue: PatientSidebarTag[] = tags.data ?? [];

    if (!core.data && tagsValue.length === 0 && !note.data) {
      return EMPTY_PATIENT_DETAIL;
    }

    return {
      patient: core.data ?? null,
      tags: tagsValue,
      note: note.data ?? '',
      memberships: [] as PatientSidebarMembership[],
      upcomingAppointments: [] as PatientSidebarAppointment[],
      recentAppointments: [] as PatientSidebarAppointment[],
    };
  }, [core.data, tags.data, note.data]);

  // Loading reflects the core query — sections render their own skeletons
  // independently. This keeps the contact header from blocking on slower
  // sections like memberships or appointments.
  const loading = core.isLoading;
  const failed = core.isError && !core.data;

  return { detail, loading, failed };
}
